import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import isEqual from 'react-fast-compare';

import {getConfigShapeAtConfigShapePath} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettingsUtils';
import * as PipelineDAO from 'pipelines/network/PipelineDAO';
import {GetPipelineConfigSpecType, getStepTypeConfigShape} from 'pipelines/StepShapeUtils';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {isServerRequestError} from 'util/Net';
import {JSONValue} from 'util/Types';

import makeInitialConfigState from './initial-values/makeInitialConfigState';
import {STEP_CONFIG_SETTINGS} from './step-config-settings/StepConfigSettings';
import {
  BACKEND_FIELD_GENERATOR_CONFIG,
  FIELD_ORDER_UI_SCHEMA_KEY,
  isShapeUISchema,
  ShapeUISchema,
  StepConfigUISchema,
} from './StepConfigEditorTypes';
import {ConfigEditorStepType} from './stepTypeIsConfigEditorSupported';

const NO_FIELDS: ReadonlyArray<string> = [];

const BACKEND_FIELD_GENERATION_DEBOUNCE_MS = 500;

interface BackendGeneratedShape {
  stepType: ConfigEditorStepType;
  stepVersion: number;
  stepId: string | undefined;
  stepShape: GetPipelineConfigSpecType<'shape'>;
  // False for the shape built from the saved config.
  isLive: boolean;
  // The trigger and live field values the shape was generated for. The shape is only reused while they match,
  // since e.g. runtime values saved as named fields don't fit a shape generated for a free-form list.
  triggerFieldValues: Record<string, JSONValue | undefined>;
  liveFieldValues: Record<string, JSONValue | undefined>;
  // The config generated with the shape, shown when the pane is read-only and its parent doesn't keep it.
  config: Record<string, JSONValue>;
  // The values of the live fields the user selects from (enums) that the shape was generated for. The shape's
  // `__liveFieldsWarning` is about them, so it's only shown while they match.
  liveSelectionValues: Record<string, JSONValue | undefined>;
  // The step's connected inputs the shape was generated for (see `connectedInputNames`).
  connectedInputsKey: string;
}

// A cache kept across remounts, so that reopening a step with a saved shape can show its last generated shape right
// away. Each mounted pane keeps its own shape and only uses a cached one that fits its config. Panes for the same
// step (e.g. the sidebar and the flowchart's hover preview) share this cache; only editable panes write it, and no
// pane clears it.
const backendGeneratedShapeGlobalState = makeUseGlobalClientState<BackendGeneratedShape | null>(null);

function getFieldValues(
  config: Record<string, JSONValue>,
  fields: ReadonlyArray<string>
): Record<string, JSONValue | undefined> {
  return Object.fromEntries(fields.map((field) => [field, config[field]]));
}

/**
 * The config with `fields` taken from `source`, including removing those `source` doesn't have.
 * @package
 */
export function withFieldsFrom(
  config: Record<string, JSONValue>,
  source: Record<string, JSONValue>,
  fields: ReadonlyArray<string>
): Record<string, JSONValue> {
  const merged = {...config};
  fields.forEach((field) => {
    if (Object.hasOwn(source, field)) {
      merged[field] = source[field];
    } else {
      delete merged[field];
    }
  });
  return merged;
}

/** The selected option's name as shown in its dropdown, or the value if the shape doesn't name it. */
function getSelectedOptionName(
  stepShape: GetPipelineConfigSpecType<'shape'> | null,
  field: string,
  value: JSONValue | undefined
): string {
  const fieldShape = stepShape?.fields[field];
  const choice =
    fieldShape?.kind === 'enum'
      ? fieldShape.choices.find((option) => (typeof option === 'object' ? option.value : option) === value)
      : undefined;
  if (choice != null && typeof choice === 'object' && choice.displayName != null) {
    return choice.displayName;
  }
  return String(value ?? '');
}

/** Whether the stored shape was generated for the config's trigger and live field values. */
function storedShapeFitsConfig(
  stored: BackendGeneratedShape,
  config: Record<string, JSONValue>,
  triggerFields: ReadonlyArray<string>,
  liveFields: ReadonlyArray<string>
): boolean {
  return (
    isEqual(stored.triggerFieldValues, getFieldValues(config, triggerFields)) &&
    isEqual(stored.liveFieldValues, getFieldValues(config, liveFields))
  );
}

interface LiveRequest {
  // The trigger field whose change started the request, or null when the step opened.
  triggerField: string | null;
}

function getLiveSelectionFields(
  stepShape: GetPipelineConfigSpecType<'shape'>,
  liveFields: ReadonlyArray<string>
): ReadonlyArray<string> {
  return liveFields.filter((field) => stepShape.fields[field]?.kind === 'enum');
}

/** @package */
export const REQUESTING_LIVE_FIELDS_BANNER_TEXT =
  'Loading current settings from the instrument. Instrument settings are disabled while the connection is established.';

/** @package */
export const BACKEND_FIELDS_REQUEST_FAILED_BANNER_TEXT =
  'Could not load the options for this step. Reopen the step to try again.';

interface UseLiveStepShapeParams {
  stepType: ConfigEditorStepType;
  stepVersion: number;
  stepId: string | undefined;
  clientStateId: string;
  config: Record<string, JSONValue> | null;
  setConfig: (newConfig: Record<string, JSONValue>) => void;
  readOnly: boolean;
  /**
   * The names of the step's inputs with saved incoming edges. The backend can shape the config by them, e.g.
   * Opentrons disables protocol selection while a protocol file input is connected, so a shape is only used for
   * the inputs it was generated for, and the shape is requested again when they change.
   */
  connectedInputNames: ReadonlyArray<string>;
}

export interface LiveStepShape {
  /** The shape to edit the config with: the generated one, or the step's static shape until one arrives. */
  configShape: GetPipelineConfigSpecType<'shape'>;
  /**
   * The config to show. A read-only pane shows the fields that depend on the shape from the config generated with
   * it, since its parent may not keep that config.
   */
  config: Record<string, JSONValue> | null;
  /** Sets the config the user edited, and requests the live shape again when a trigger field changed. */
  setConfig: (newConfig: Record<string, JSONValue>) => void;
  /** Top-level fields to show read-only: all of them until a shape arrives, then the locked live fields. */
  disabledFields: ReadonlyArray<string>;
  /** What's loading, while a request that waits on the instrument is in flight. */
  loadingMessage: string | null;
  warnings: ReadonlyArray<string>;
  /**
   * True while the live shape for a trigger field the user changed (e.g. the variables of a newly selected method)
   * is loading. The config's live fields still belong to the previous selection until it arrives.
   */
  isSelectionLoading: boolean;
}

/** Whether the step type's UI schema sets `hasSavedShape`, i.e. `useLiveStepShape` shows its saved shape. */
export function stepTypeHasSavedShape(stepType: ConfigEditorStepType, stepVersion: number): boolean {
  return (
    STEP_CONFIG_SETTINGS[stepType][stepVersion]?.uiSchema?.[BACKEND_FIELD_GENERATOR_CONFIG]?.hasSavedShape ===
    true
  );
}

/**
 * For step types whose UI schema sets `hasSavedShape`: shows the shape the backend builds from the saved config right
 * away, and loads the live shape, which waits on an instrument, in the background. Only the live fields wait on
 * it. A read-only pane only requests the saved shape.
 *
 * Returns null for other step types.
 */
export default function useLiveStepShape({
  stepType,
  stepVersion,
  stepId,
  clientStateId,
  config,
  setConfig,
  readOnly,
  connectedInputNames,
}: UseLiveStepShapeParams): LiveStepShape | null {
  const [cachedBackendGeneratedShape, setCachedBackendGeneratedShape] =
    backendGeneratedShapeGlobalState.useGlobalClientState(clientStateId);
  const [ownBackendGeneratedShape, setOwnBackendGeneratedShape] = useState<BackendGeneratedShape | null>(
    null
  );

  const uiSchema = STEP_CONFIG_SETTINGS[stepType][stepVersion]?.uiSchema;
  const backendFieldGeneratorConfig = uiSchema?.[BACKEND_FIELD_GENERATOR_CONFIG];
  const hasSavedShape = stepTypeHasSavedShape(stepType, stepVersion);
  const backendTriggerFields: ReadonlyArray<string> = backendFieldGeneratorConfig?.triggerFields ?? NO_FIELDS;
  const liveFields: ReadonlyArray<string> = backendFieldGeneratorConfig?.liveFields ?? NO_FIELDS;
  const allFields: ReadonlyArray<string> =
    uiSchema != null && isShapeUISchema(uiSchema as StepConfigUISchema)
      ? (uiSchema as ShapeUISchema)[FIELD_ORDER_UI_SCHEMA_KEY]
      : NO_FIELDS;
  const currentConfig = useMemo(
    () => config ?? (hasSavedShape ? makeInitialConfigState(stepType, stepVersion) : {}),
    [config, hasSavedShape, stepType, stepVersion]
  );
  const connectedInputsKey = [...new Set(connectedInputNames)].sort((a, b) => a.localeCompare(b)).join(',');
  const isShapeForStep = (shape: BackendGeneratedShape | null): shape is BackendGeneratedShape =>
    shape != null &&
    shape.stepType === stepType &&
    shape.stepVersion === stepVersion &&
    shape.stepId === stepId &&
    shape.connectedInputsKey === connectedInputsKey;
  // This pane's own shape, kept while a trigger field change loads. Until it has one for the current step (e.g.
  // before the mount effect, or when a reused pane switches steps), a cached shape is only shown if it fits the
  // step's config: an editor with a shape that doesn't fit could normalize away the config's values.
  const backendGeneratedShape = isShapeForStep(ownBackendGeneratedShape)
    ? ownBackendGeneratedShape
    : hasSavedShape &&
        isShapeForStep(cachedBackendGeneratedShape) &&
        storedShapeFitsConfig(cachedBackendGeneratedShape, currentConfig, backendTriggerFields, liveFields)
      ? cachedBackendGeneratedShape
      : null;
  const stepShapeOverride = backendGeneratedShape?.stepShape ?? null;
  const displayedShapeRef = useRef<BackendGeneratedShape | null>(null);
  displayedShapeRef.current = backendGeneratedShape;
  const [liveRequest, setLiveRequest] = useState<LiveRequest | null>(null);
  const isRequestingLiveFields = liveRequest != null;
  const [backendFieldsRequestFailed, setBackendFieldsRequestFailed] = useState<boolean>(false);
  // Set when the backend couldn't generate a shape (e.g. it couldn't look up connections) while one was shown. The
  // shown shape and config stay, with this warning, and the live fields stay locked.
  const [unavailableShapeWarning, setUnavailableShapeWarning] = useState<string | null>(null);
  const shapeFields = stepShapeOverride?.fields as Record<string, unknown> | undefined;
  // Set when the instrument couldn't be reached. `__liveFieldsWarning` is set when it was reached but a later
  // request failed, e.g. for the selected method's variables, so its options can still be used.
  const connectionWarning = shapeFields?.__connectionWarning;
  // Hidden once the user selects another option, e.g. a method that isn't the missing one, on step types where
  // that doesn't request the live shape again.
  const liveFieldsWarning =
    backendGeneratedShape != null &&
    isEqual(
      backendGeneratedShape.liveSelectionValues,
      getFieldValues(currentConfig, Object.keys(backendGeneratedShape.liveSelectionValues))
    )
      ? shapeFields?.__liveFieldsWarning
      : undefined;
  const shapeHasConnectionWarning = typeof connectionWarning === 'string' || unavailableShapeWarning != null;
  const connectionField = backendFieldGeneratorConfig?.connectionField;
  const hasNoConnection = connectionField != null && !config?.[connectionField];
  // The live fields can only be edited with options from the instrument: not on the saved shape, not without a
  // connection, not when the live shape couldn't reach the instrument, and not when the live request failed.
  // Selecting another connection requests the live shape again. While the user's change to a trigger field
  // loads, only the other live fields wait on it; the changed field stays usable, and changing it again requests
  // the live shape again.
  const lockedLiveFields: ReadonlyArray<string> = useMemo(() => {
    // A read-only pane is disabled as a whole, and never waits on the live shape.
    if (backendGeneratedShape == null || readOnly) {
      return NO_FIELDS;
    }
    if (liveRequest?.triggerField != null) {
      return liveFields.filter((field) => field !== liveRequest.triggerField);
    }
    return backendGeneratedShape.isLive === false ||
      hasNoConnection ||
      shapeHasConnectionWarning ||
      backendFieldsRequestFailed
      ? liveFields
      : NO_FIELDS;
  }, [
    backendGeneratedShape,
    liveRequest,
    liveFields,
    hasNoConnection,
    shapeHasConnectionWarning,
    backendFieldsRequestFailed,
    readOnly,
  ]);
  const liveLoadingMessages = backendFieldGeneratorConfig?.liveLoadingMessages;
  const triggerFieldMessage =
    liveRequest?.triggerField != null
      ? liveLoadingMessages?.byTriggerField?.[liveRequest.triggerField]
      : undefined;
  const liveLoadingMessage =
    liveRequest?.triggerField != null && triggerFieldMessage != null
      ? triggerFieldMessage(
          getSelectedOptionName(
            stepShapeOverride,
            liveRequest.triggerField,
            config?.[liveRequest.triggerField]
          )
        )
      : (liveLoadingMessages?.onOpen ?? REQUESTING_LIVE_FIELDS_BANNER_TEXT);

  const requestCounterRef = useRef(0);
  // Aborted once its request is superseded or the pane closes. Live requests can take minutes, and aborting frees
  // the browser's connection for other requests; the server still finishes the request.
  const inFlightRequestRef = useRef<AbortController | null>(null);
  const abortInFlightRequest = useCallback((): void => {
    inFlightRequestRef.current?.abort();
    inFlightRequestRef.current = null;
  }, []);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userEditCountRef = useRef(0);
  const latestConfigRef = useRef<Record<string, JSONValue> | null>(null);
  latestConfigRef.current = config;
  const getLatestConfig = useCallback(
    (): Record<string, JSONValue> => latestConfigRef.current ?? makeInitialConfigState(stepType, stepVersion),
    [stepType, stepVersion]
  );

  /**
   * - `live`: request the live shape. Only the live fields other than `triggerField` wait on it.
   * - `background`: request the live shape while the editor stays usable.
   * - `savedFirst`: request the shape built from the saved config, then the live one in the background.
   *
   * A failed request keeps the current shape and config and shows a warning.
   */
  const generateBackendFields = useCallback(
    (
      newConfig: Record<string, JSONValue>,
      mode: 'live' | 'background' | 'savedFirst',
      triggerField: string | null = null
    ) => {
      const requestId = ++requestCounterRef.current;
      const isCurrent = (): boolean => requestId === requestCounterRef.current;

      async function request(
        requestConfig: Record<string, JSONValue>,
        requestMode: 'live' | 'background' | 'savedFirst'
      ): Promise<void> {
        const isBackground = requestMode === 'background';
        const isLive = requestMode !== 'savedFirst';
        const editCountAtRequest = userEditCountRef.current;
        // A read-only pane only requests the saved shape, which doesn't wait on an instrument.
        if (!readOnly) {
          setLiveRequest({triggerField});
        }
        setBackendFieldsRequestFailed(false);
        setUnavailableShapeWarning(null);
        abortInFlightRequest();
        const abortController = new AbortController();
        inFlightRequestRef.current = abortController;
        let response: PipelineDAO.GenerateStepConfigResponse;
        try {
          response = await PipelineDAO.generateStepConfig(
            getStepTypeConfigShape(stepType, stepVersion),
            requestConfig,
            stepType,
            stepId,
            isLive,
            abortController.signal
          );
        } catch (e) {
          // A superseded request, e.g. one aborted for a newer request or a closed pane, no longer matters.
          if (!isCurrent()) {
            return;
          }
          setBackendFieldsRequestFailed(true);
          if (!isServerRequestError(e)) {
            throw e;
          }
          return;
        } finally {
          if (isCurrent()) {
            setLiveRequest(null);
          }
        }
        if (!isCurrent()) {
          return;
        }
        const responseFields = response.stepShape.fields as Record<string, unknown>;
        if (responseFields.__shapeUnavailable === true && displayedShapeRef.current != null) {
          const warning = responseFields.__connectionWarning;
          setUnavailableShapeWarning(
            typeof warning === 'string' ? warning : BACKEND_FIELDS_REQUEST_FAILED_BANNER_TEXT
          );
          return;
        }
        if (isBackground && userEditCountRef.current !== editCountAtRequest) {
          // The shape and config are generated together, and this shape may not fit the edited config (e.g.
          // runtime variables switching between typed fields and a free-form list), so generate them again for
          // the edited config.
          await request(getLatestConfig(), 'background');
          return;
        }
        // The user can edit the fields that don't depend on the response while it loads. The response only
        // decides the live fields and the trigger fields (e.g. a single connection it selects), so the other edits
        // are kept. A user change to a trigger field would have started a newer request.
        const responseConfig =
          userEditCountRef.current !== editCountAtRequest
            ? withFieldsFrom(getLatestConfig(), response.config, [...liveFields, ...backendTriggerFields])
            : response.config;
        const generatedShape: BackendGeneratedShape = {
          stepType,
          stepVersion,
          stepId,
          connectedInputsKey,
          stepShape: response.stepShape,
          isLive: response.isLive !== false,
          triggerFieldValues: getFieldValues(responseConfig, backendTriggerFields),
          liveFieldValues: getFieldValues(responseConfig, liveFields),
          config: responseConfig,
          liveSelectionValues: getFieldValues(
            responseConfig,
            getLiveSelectionFields(response.stepShape, liveFields)
          ),
        };
        setOwnBackendGeneratedShape(generatedShape);
        // Only editable panes update the cache. A read-only pane's saved-config shape could otherwise replace a
        // shape another pane is working from, e.g. the sidebar's for an unsaved method.
        if (!readOnly) {
          setCachedBackendGeneratedShape(generatedShape);
        }
        setConfig(responseConfig);
        if (response.isLive === false && !readOnly) {
          await request(responseConfig, 'background');
        }
      }
      request(newConfig, mode);
    },
    [
      stepType,
      stepId,
      stepVersion,
      backendTriggerFields,
      liveFields,
      setCachedBackendGeneratedShape,
      setConfig,
      abortInFlightRequest,
      getLatestConfig,
      readOnly,
      connectedInputsKey,
    ]
  );

  const debouncedGenerateBackendFields = useCallback(
    (newConfig: Record<string, JSONValue>, triggerField: string) => {
      if (debounceTimerRef.current != null) {
        clearTimeout(debounceTimerRef.current);
      }
      // Invalidate any in-flight request so it doesn't overwrite state during the debounce window
      requestCounterRef.current++;
      abortInFlightRequest();
      debounceTimerRef.current = setTimeout(() => {
        generateBackendFields(newConfig, 'live', triggerField);
      }, BACKEND_FIELD_GENERATION_DEBOUNCE_MS);
    },
    [generateBackendFields, abortInFlightRequest]
  );
  useEffect(
    () => () => {
      if (debounceTimerRef.current != null) {
        clearTimeout(debounceTimerRef.current);
      }
      requestCounterRef.current++;
      abortInFlightRequest();
    },
    [abortInFlightRequest]
  );

  useEffect(() => {
    // A shape generated earlier for the same step and trigger and live values is shown right away and refreshed in
    // the background, so reopening a configured step doesn't wait on the backend.
    const initialConfig = hasSavedShape ? (config ?? makeInitialConfigState(stepType, stepVersion)) : null;
    const canReuseShape =
      initialConfig != null &&
      isShapeForStep(cachedBackendGeneratedShape) &&
      storedShapeFitsConfig(cachedBackendGeneratedShape, initialConfig, backendTriggerFields, liveFields);
    setOwnBackendGeneratedShape(canReuseShape ? cachedBackendGeneratedShape : null);
    setLiveRequest(null);
    setBackendFieldsRequestFailed(false);
    setUnavailableShapeWarning(null);
    if (debounceTimerRef.current != null) {
      clearTimeout(debounceTimerRef.current);
    }
    requestCounterRef.current++;
    abortInFlightRequest();
    if (initialConfig == null) {
      return;
    }
    if (!readOnly) {
      generateBackendFields(initialConfig, canReuseShape ? 'background' : 'savedFirst');
    } else if (!canReuseShape) {
      // A read-only pane, e.g. the flowchart's hover preview, can't be edited, so it doesn't load the live
      // options. It still needs the saved shape to show the saved method or protocol and runtime values.
      generateBackendFields(initialConfig, 'savedFirst');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- config, generateBackendFields, and other deps intentionally excluded to avoid infinite re-render loops
  }, [stepType, stepId, stepVersion, readOnly, connectedInputsKey]);

  const handleSetConfig = useCallback(
    (newConfig: Record<string, JSONValue>) => {
      const oldConfig = getLatestConfig();
      userEditCountRef.current++;
      latestConfigRef.current = newConfig;
      setConfig(newConfig);
      const changedTriggerField = backendTriggerFields.find(
        (field) => !isEqual(oldConfig[field], newConfig[field])
      );
      if (changedTriggerField == null) {
        return;
      }
      const currentConfigShape = stepShapeOverride ?? getStepTypeConfigShape(stepType, stepVersion);
      const fieldSpec = getConfigShapeAtConfigShapePath([changedTriggerField], currentConfigShape, newConfig);
      if (fieldSpec.kind === 'string') {
        debouncedGenerateBackendFields(newConfig, changedTriggerField);
      } else {
        generateBackendFields(newConfig, 'live', changedTriggerField);
      }
    },
    [
      getLatestConfig,
      setConfig,
      backendTriggerFields,
      stepShapeOverride,
      stepType,
      stepVersion,
      debouncedGenerateBackendFields,
      generateBackendFields,
    ]
  );

  if (!hasSavedShape) {
    return null;
  }

  // The warnings are about the last attempt to reach the instrument, so they're hidden while a new attempt runs.
  // When the last request failed or couldn't generate a shape, the shown shape's own warnings are about an earlier
  // attempt (e.g. another connection), so only that failure is shown.
  const shapeWarnings =
    isRequestingLiveFields || backendFieldsRequestFailed
      ? []
      : unavailableShapeWarning != null
        ? [unavailableShapeWarning]
        : [connectionWarning, liveFieldsWarning].filter(
            (warning): warning is string => typeof warning === 'string'
          );

  return {
    configShape: stepShapeOverride ?? getStepTypeConfigShape(stepType, stepVersion),
    config:
      readOnly && backendGeneratedShape != null && config != null
        ? withFieldsFrom(currentConfig, backendGeneratedShape.config, [
            ...liveFields,
            ...backendTriggerFields,
          ])
        : config,
    setConfig: handleSetConfig,
    // Until a shape arrives there are no options to show, so the whole form waits on it, including after the
    // request failed.
    disabledFields: backendGeneratedShape == null ? allFields : lockedLiveFields,
    loadingMessage: isRequestingLiveFields ? liveLoadingMessage : null,
    warnings: [
      ...(backendFieldsRequestFailed ? [BACKEND_FIELDS_REQUEST_FAILED_BANNER_TEXT] : []),
      ...shapeWarnings,
    ],
    isSelectionLoading: liveRequest?.triggerField != null,
  };
}
